// src/components/Inv-Tracker/hooks/useInvTrackerController.js
// ============================================================================
// BLOCK ITR.HOOK.001 — Inventory Tracker controller hook
// ----------------------------------------------------------------------------
// PURPOSE
// - Owns remote data loading, filter state, selection state, and write actions.
// - Keeps view components focused on rendering.
// ============================================================================

import React from 'react';

import {
  fetchInventoryTransactions,
  fetchReferenceDates,
  updateReviewRows,
} from '../api/invTrackerApi.js';

// ============================================================================
// BLOCK ITR.HOOK.001.01 — Feature constants
// ----------------------------------------------------------------------------
// PURPOSE
// - Centralize defaults used by the page shell.
// ============================================================================
const DEFAULT_FILTERS = {
  warehouseCode: '000',
  referenceDate: '',
  fromDate: '',
  toDate: '',
  search: '',
  userCode: '',
  reviewFilter: 'active',
};

function trimStr(value) {
  return String(value ?? '').trim();
}

function buildRowSelectionMap(nextSet) {
  const map = {};
  [...nextSet].forEach((key) => {
    map[key] = true;
  });
  return map;
}

export default function useInvTrackerController() {
  // Keep the top-bar filter values in one object for easy updates.
  const [filters, setFilters] = React.useState(DEFAULT_FILTERS);

  // Store the active tree payload returned by the backend.
  const [items, setItems] = React.useState([]);

  // Store available reference dates for the dropdown.
  const [referenceDates, setReferenceDates] = React.useState([]);

  // Store top-level loading / submitting states separately.
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);

  // Store error text for alert rendering.
  const [error, setError] = React.useState('');

  // Store aggregate counts for quick page metrics.
  const [totals, setTotals] = React.useState({
    totalSourceRows: 0,
    totalVisibleRows: 0,
    statusCounts: { open: 0, follow_up: 0, cleared: 0 },
  });

  // Store selected source keys in a Set for fast add/remove checks.
  const [selectedKeys, setSelectedKeys] = React.useState(() => new Set());

  // Store selection as a plain object too so rendering can cheaply check rows.
  const [selectedRowMap, setSelectedRowMap] = React.useState({});

  // Mirror the latest filters in a ref so async loaders can safely read the
  // current values without forcing callback identity churn.
  const filtersRef = React.useRef(DEFAULT_FILTERS);

  React.useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const loadReferenceDates = React.useCallback(async (warehouseCode) => {
    const rows = await fetchReferenceDates({ warehouseCode, limit: 12 });
    setReferenceDates(rows);
    return rows;
  }, []);

  const loadTransactions = React.useCallback(
    async (overrideFilters = null) => {
      const nextFilters = overrideFilters || filtersRef.current;

      setLoading(true);
      setError('');

      try {
        // Load reference dates first so a missing selected reference date can be
        // defaulted to the newest available physical count date.
        const availableDates = await loadReferenceDates(nextFilters.warehouseCode);
        const effectiveReferenceDate =
          trimStr(nextFilters.referenceDate) || trimStr(availableDates[0]);

        const result = await fetchInventoryTransactions({
          warehouseCode: trimStr(nextFilters.warehouseCode) || undefined,
          referenceDate: effectiveReferenceDate || undefined,
          fromDate: trimStr(nextFilters.fromDate) || undefined,
          toDate: trimStr(nextFilters.toDate) || undefined,
          search: trimStr(nextFilters.search) || undefined,
          userCode: trimStr(nextFilters.userCode) || undefined,
          reviewFilter: trimStr(nextFilters.reviewFilter) || undefined,
        });

        setItems(result.items);
        setTotals({
          totalSourceRows: result.totalSourceRows,
          totalVisibleRows: result.totalVisibleRows,
          statusCounts: result.statusCounts,
        });

        // Keep the filters synced with the resolved reference date from the API.
        setFilters((prev) => ({
          ...prev,
          ...nextFilters,
          referenceDate: result.referenceDate || effectiveReferenceDate || '',
          fromDate: trimStr(nextFilters.fromDate) || '',
          toDate: trimStr(nextFilters.toDate) || '',
        }));

        // Reset row selection whenever the source payload changes.
        setSelectedKeys(new Set());
        setSelectedRowMap({});
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Failed to load inventory tracker.');
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [loadReferenceDates]
  );

  React.useEffect(() => {
    loadTransactions(DEFAULT_FILTERS);
  }, [loadTransactions]);

  const setFilterValue = React.useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const toggleSelectedKey = React.useCallback((key, checked) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);

      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }

      setSelectedRowMap(buildRowSelectionMap(next));
      return next;
    });
  }, []);

  const replaceSelectedKeys = React.useCallback((keys) => {
    const nextSet = new Set((Array.isArray(keys) ? keys : []).filter(Boolean));
    setSelectedKeys(nextSet);
    setSelectedRowMap(buildRowSelectionMap(nextSet));
  }, []);

  const clearSelection = React.useCallback(() => {
    setSelectedKeys(new Set());
    setSelectedRowMap({});
  }, []);

  const applyReviewStatus = React.useCallback(
    async (status, reviewNote = '') => {
      const sourceKeys = [...selectedKeys];

      if (!sourceKeys.length) {
        return;
      }

      setSubmitting(true);
      setError('');

      try {
        await updateReviewRows({
          source_keys: sourceKeys,
          status,
          review_note: trimStr(reviewNote) || undefined,
        });

        await loadTransactions({
          ...filters,
        });
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Failed to update review status.');
      } finally {
        setSubmitting(false);
      }
    },
    [filters, loadTransactions, selectedKeys]
  );

  const refresh = React.useCallback(() => {
    loadTransactions({ ...filters });
  }, [filters, loadTransactions]);

  return {
    loading,
    submitting,
    error,
    filters,
    items,
    referenceDates,
    totals,
    selectedKeys,
    selectedRowMap,
    setFilterValue,
    toggleSelectedKey,
    replaceSelectedKeys,
    clearSelection,
    applyReviewStatus,
    refresh,
    reload: loadTransactions,
  };
}
